import React, { useState } from 'react';
import { loader } from 'graphql.macro';
import moment from 'moment-timezone';
import { useLazyQuery, useQuery, useMutation, useSubscription } from '@apollo/react-hooks';
import { ChangePolicyDropdown } from '.';
import useFeatureFlag from '../../util/hooks/useFeatureFlag';
import { formatDateObj, formatDateAsString } from '../../util/formatters';
import { boolean } from '@storybook/addon-knobs';
import { string } from 'prop-types';
import { minDate } from '../cancelPolicy/cancelPolicyForm/formFields/effectiveDate';

const get_transaction_details = loader(
  './graphql/Get_Transaction_Detail.graphql'
);

const on_process_result = loader('./graphql/Run_Subscription_Result.graphql');

const make_a_policy_change = loader('./graphql/Make_A_Policy_Change.graphql');

const policy_change_with_document_upload = loader(
  './graphql/Policy_Change_With_Document_Upload.graphql'
);

const upload_policy_document_mutation = loader(
  '../gpaCancelPolicy/graphql/Cancel_Policy_Upload_Policy_Document.graphql'
);

export const updateDateFormat = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const setMinAndMaxEffectiveDate = (
  latestPolicy: any,
  isCppLine: boolean,
  isFarmRanch: boolean,
  isCommercialLine: boolean,
  isWorkerCompLine: boolean,
  policyTransactionArray: any
) => {
  let maxDate, minDate;
  maxDate = moment(latestPolicy?.expirationDate, "YYYY-MM-DD").toDate();
  var isSubmissionInForce = true;
  var isRenewalTermActive = false;
  var policyTransaction;
  var policyDisplayStatus = "";
  var policyStatus = "";
  var policyType = "";
  if (policyTransactionArray.length > 1) {
    for (var index in policyTransactionArray) {
      policyTransaction = policyTransactionArray[index];
      policyDisplayStatus = policyTransaction.policyDisplayStatus.replace(/ /g, '').toUpperCase();
      policyType = policyTransaction.type.replace(/ /g, '').toUpperCase();
      policyStatus = policyTransaction.status.replace(/ /g, '').toUpperCase();
      if (policyType === "RENEWAL" && policyStatus === "ISSUED" &&
        (policyDisplayStatus === "INFORCE" || policyDisplayStatus === "SCHEDULED")) {
        var tempDate = moment(policyTransaction.closeDate, "YYYY-MM-DD").utc().toDate();
        if (tempDate > maxDate) {
          maxDate = tempDate;
        }
        isRenewalTermActive = true;
      }
      if (policyType === "SUBMISSION" && policyDisplayStatus === "EXPIRED" && policyStatus === "ISSUED") {
        isSubmissionInForce = false;
      }
    }
  }
  if (isCommercialLine) {
    maxDate = moment(latestPolicy?.expirationDate, "YYYY-MM-DD").toDate();
    maxDate.setHours(0, 0, 0, 0);
    minDate = moment(latestPolicy?.effectiveDate).toDate();
    minDate.setHours(0, 0, 0, 0);
  } else if (isCppLine || isWorkerCompLine || isFarmRanch) {
    maxDate = moment(latestPolicy?.expirationDate, "YYYY-MM-DD").add(-1, 'day').toDate();
    maxDate.setHours(0, 0, 0, 0);
    minDate = moment(latestPolicy?.effectiveDate).toDate();
    minDate.setHours(0, 0, 0, 0);
  } else {
    maxDate = moment(latestPolicy?.expirationDate, "YYYY-MM-DD").utc().toDate();
    maxDate = moment(maxDate).add(-1, 'day').utc().toDate();
    maxDate.setHours(0, 0, 0, 0);
    minDate = moment().add(-14, 'day').utc().toDate();
    minDate.setHours(0, 0, 0, 0);
  }
  //PL - Policy Change - GPA Dashboard - Policy change effective date should default to Blank field
  // if($scope.policyBusinessLine === "HomeownersLine_HOE" || $scope.policyBusinessLine === "PersonalAutoLine" || $scope.policyBusinessLine === "PersonalUmbrellaLine_PUE"){
  //     $scope.policyChangeView.effectiveDate.value = '';
  //     $scope.invalidEffectiveDate = true;
  //     $scope.effectiveDateErrorMessage = 'This is a required field';
  // }
  // else
  //     $scope.policyChangeView.effectiveDate.value = moment().utc().toDate().toLocalDate();

  let policyEffectiveDate = moment(latestPolicy.effectiveDate, "YYYY-MM-DD").utc().toDate();
  policyEffectiveDate.setHours(0, 0, 0, 0);
  if (policyEffectiveDate > minDate && isSubmissionInForce && !isRenewalTermActive) {
    minDate = policyEffectiveDate;
  }
  // This will only work for policies with future renewals as per SLGWCLST-10091
  // making this for commercial only
  // if (isCommercialLine && latestPolicy?.policyHasFutureRenewal && latestPolicy?.isFutureRenewalUnbound !== undefined &&
  //   !latestPolicy?.isFutureRenewalUnbound)  {
  //     updateCalendarMinDate(latestPolicy?.currentTermStartDate, moment());
  // }

  return { maxDate, minDate };
};

const findRenewalDate = (
  policy: any,
  snapshots: any
) => {
  if (policy?.status !== 'InForce') return null;
  if (!snapshots?.length) return null;
  const currentDate = moment.tz('America/New_York');
  const filteredSnapshots = snapshots.filter(
    (
      snapshot: any
    ): snapshot is any => {
      if (!snapshot?.effectiveDate) return false;
      const reqDate = formatDateObj(snapshot?.effectiveDate);
      if (reqDate) {
        const snapshotDate = moment.tz(
          reqDate,
          'YYYY-MM-DD',
          'America/New_York'
        );
        return snapshotDate.isAfter(currentDate) && snapshot.status === 'RENEWAL';
      }
      return false;
    }
  );
  if (filteredSnapshots.length) return formatDateObj(filteredSnapshots[0].effectiveDate);
  return null;
};

const createTermObj = (minDate: any, maxDate: any) => {
  let itmResult: termObj = { 'isSelected': true, 'isDisabled': false, 'minDate': '', 'maxDate': '', 'textDate': '' };
  itmResult['isSelected'] = true;
  itmResult['isDisabled'] = false;
  itmResult['minDate'] = minDate;
  itmResult['maxDate'] = maxDate;
  itmResult['textDate'] = formatDateAsString((minDate)) + '-' + formatDateAsString((maxDate));
  return itmResult;
};

const renewalTerms = (
  policyHasFutureRenewalDate: boolean,
  isFutureRenewalUnbound: boolean,
  isCommercialLine: boolean,
  result: any,
) => {
  let termsList = [];
  if (result && !!policyHasFutureRenewalDate && isCommercialLine) {
    const currentTermStartDate = result?.result?.currentTermStartDate;
    const currentTermEndDate = result?.result?.currentTermEndDate;
    const renewalTermStartDate = result?.result?.renewalTermStartDate;
    const renewalTermEndDate = result?.result?.renewalTermEndDate;
    if (currentTermStartDate && currentTermEndDate) {
      termsList.push(createTermObj(formatDateObj(currentTermStartDate), formatDateObj(currentTermEndDate)));
    }
    if (renewalTermStartDate && renewalTermEndDate) {
      termsList.push(createTermObj(formatDateObj(renewalTermStartDate), formatDateObj(renewalTermEndDate)));
    }
  }
  if (result && isCommercialLine && isFutureRenewalUnbound) {
    termsList[0].isSelected = true;
    termsList[0].isDisabled = true;
  }
  return termsList;
};

export interface changePolicyDataContainerProps {
  readOnlyAccess: boolean;
  readOnlyFeatureFlag: boolean;
  accountNumber: string;
  policyNumber: string;
  result: any;
  type: string;
  code: string;
  query_env: string;
  uploadToken: string;
  openChanges: boolean;
}

export interface termObj {
  isSelected: boolean;
  isDisabled: boolean;
  minDate: string;
  maxDate: string;
  textDate: any;
}

const checkRenewalForChangeExists = (transactionData: any) => {
  // return transactionData.some((transaction: any) =>
  return (
    transactionData?.displayType === "Renewal" && (transactionData?.policyDisplayStatus === "Renewing" || transactionData?.policyDisplayStatus === "Scheduled")
  );
};

const calculateActivePolicyChanges = (transactionData: any) => {
  let bResult = false;
  if ((transactionData !== undefined) && transactionData.length > 0) {
    var lstOpenPolicyChanges = transactionData.filter((itmTransaction: any) => {
      return (itmTransaction !== undefined) && itmTransaction.type === 'PolicyChange' && !itmTransaction.isChangeOverMigration && (itmTransaction.status === 'Draft' || itmTransaction.status === 'Quoted');
    });
    bResult = (lstOpenPolicyChanges !== undefined) && lstOpenPolicyChanges.length > 0;
  }
  return bResult;
};

const checkIfMigrationExists = (transactionData: any, status: string) => {
  let isMigratedOrMigrating = false;
  let isMigrating = false;
  if ((transactionData !== undefined) && transactionData.length > 0) {
    var checkMigration = transactionData.filter((itmTransaction: any) => {
      return (itmTransaction !== undefined) && itmTransaction.type === 'Migration' &&  (itmTransaction.status === status);
        // || itmTransaction.status === 'Migrating');
    });
    isMigratedOrMigrating = (checkMigration !== undefined) && checkMigration.length >0;
  }
  return isMigratedOrMigrating;
};

// const checkIfMigratingExists = (transactionData: any) => {
//   // let isMigrated = false;
//   let isMigrating = false;
//   if ((transactionData !== undefined) && transactionData.length > 0) {
//     var checkMigration = transactionData.filter((itmTransaction: any) => {
//       return (itmTransaction !== undefined) && itmTransaction.type === 'Migration' &&  (itmTransaction.status === 'Migrating');
//         // || itmTransaction.status === 'Migrating');
//     });
//     isMigrating = (checkMigration !== undefined) && checkMigration.length >0;
//   }
//   return isMigrating;
// };

export default ({
  readOnlyAccess,
  readOnlyFeatureFlag,
  accountNumber,
  policyNumber,
  result,
  type,
  query_env,
  code,
  uploadToken,
  openChanges
}: changePolicyDataContainerProps) => {
  const [runSubscription, setRunSubscription] = useState<string | null>(null);
  let redirectURL: string | null | undefined = '';
  let changeResponse: any = null;
  const [getURL, getURLResponse] = useLazyQuery(make_a_policy_change, {
    onCompleted: (response) => {
      redirectURL = response?.policy_change?.redirect_url;
      if (redirectURL) {
        window.location.assign(redirectURL);
      }
      setIsLoading(false);
      setIsOpen(false);
      redirectURL = '';
    },
    onError: (error) => {
      console.log('Error is : ', error);
      setIsLoading(false);
    }
  });

  const { data: subscriptionData, error: subscriptionError, loading: subLoading } = useSubscription(
    on_process_result,
    {
      skip: !runSubscription,
      variables: {
        policy_number: policyNumber,
      },
    }
  );

  if (subscriptionData && subscriptionData?.onProcessResult) {
    changeResponse = JSON.parse(subscriptionData?.onProcessResult?.message);
  }

  const cancelPolicyTransactionResult = useQuery(get_transaction_details, {
    variables: {
      policy_number: policyNumber,
      source_system_code: code,
      query_env: query_env,
      uwReferral: true,
      product: type,
      get_transaction: true
    }
  });
  let latestTransaction = null;
  let laterRenewalExists = false;
  let activePolicyChanges = false;
  let migratedTxn = false;
  let migratingTxn =  false;
  const { data: transactionData } = cancelPolicyTransactionResult;
  if (transactionData) {
    latestTransaction = transactionData && transactionData?.refer_to_underwriter && JSON.parse(transactionData?.refer_to_underwriter?.data);
    laterRenewalExists = checkRenewalForChangeExists(latestTransaction);
    activePolicyChanges = calculateActivePolicyChanges(latestTransaction);
    migratedTxn = checkIfMigrationExists(latestTransaction, 'Migrated');
    migratingTxn = checkIfMigrationExists(latestTransaction, 'Migrating');   //TODO: Optimize
  }

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  let isFarmPolicy: boolean = false;

  const [clChangeWithDocument] = useMutation(
    policy_change_with_document_upload
  );

  const [uploadDoc, { error }] = useMutation(upload_policy_document_mutation);

  const { flagDetails: policyCenterOutageFlagDetails } = useFeatureFlag(
    'PolicyCenter-Outage'
  );

  let isWorkerCompLine = false;
  let isFarmRanch = false;
  let isCommercialLine = false;
  let isCppLine = false;
  const cppProducts = ["General Liability", "COMMERCIAL PACKAGE", "Inland Marine"];
  const farmRanchProducts = ["Farm & Ranch Line", "Farm Auto Line", "Farm Umbrella Line"];
  let renewalDate: string | null = null;
  let termList: termObj[] = [];
  let policyChangeData;
  let maxEffectiveDate;
  let minEffectiveDate;
  let status;
  let returnedMessages;
  let isCanceling = false;
  let multipleChangesIndicator;
  let latestPolicy;
  let periods;
  let isAllowedToChange = true;
  let isRenewalBlocked = false;
  let openCancellation = false;
  let scheduledCancellation = false;
  let line;
  let showRenewal = false;

  if (result?.result && result?.result?.latestPeriod) {
    const latestPeriod = result?.result || null;
    latestPolicy = result?.result?.latestPeriod || null;
    line = latestPolicy ? latestPolicy?.policyLines[0].lineOfBusinessCode : "";
    periods = result?.result?.periods || null;
    renewalDate = findRenewalDate(latestPolicy, periods);

    periods?.forEach(
      (snapshot: any) => {
        if (snapshot?.status === 'CANCELING') isCanceling = true;
      }
    );
    status = isCanceling
      ? 'CANCELING'
      : latestPolicy?.status;
    if (type === "WORKERS COMP") {
      isWorkerCompLine = true;
    } else if (cppProducts.includes(type)) {
      isCppLine = true;
    } else if (farmRanchProducts.includes(type)) {
      isFarmRanch = true;
    } else if (code !== "GWPL") {
      isCommercialLine = true;
    };
    if (latestTransaction) {
      const { maxDate, minDate } = setMinAndMaxEffectiveDate(latestPolicy, isCppLine, isFarmRanch, isCommercialLine, isWorkerCompLine, latestTransaction);
      maxEffectiveDate = updateDateFormat(maxDate);
      minEffectiveDate = updateDateFormat(minDate);
    }
  }

  if (result?.result) {
    termList = renewalTerms(result?.result?.policyHasFutureRenewal, result?.result?.isFutureRenewalUnbound, isCommercialLine, result);
    showRenewal = (result?.result?.policyHasFutureRenewal && isCommercialLine);
  }

  if (
    type === 'FARM & RANCH' ||
    type === 'FARMOWNERS AUTO' ||
    type === 'FARMOWNERS UMBRELLA'
  ) {
    isFarmPolicy = true;
  }

  isAllowedToChange = !(result?.result && result?.result?.latestPeriod?.policyLines[0].lineOfBusinessCode === 'CULine');

  if (result?.result && result?.result.policyHasFutureRenewal && result?.result.isFutureRenewalUnbound) {
    isRenewalBlocked = result?.result.policyHasFutureRenewal && result?.result.isFutureRenewalUnbound;
  }

  scheduledCancellation = result?.result && result?.result.scheduledCancellation;
  openCancellation = result?.result && result?.result.openCancellation;
  let isChangeDisabled = !isAllowedToChange || latestPolicy?.canceled || scheduledCancellation || openCancellation ;
  let changeDisabledForWC = isWorkerCompLine && openCancellation;
  let changeDisabledForCPP = isCppLine && openCancellation;

  let errorMessage: string | null | undefined;

  const {
    data: getURLResponseData,
    error: getURLResponseError
  } = getURLResponse;

  if (
    getURLResponseData &&
    !getURLResponseError &&
    getURLResponseData.account?.items
  ) {
    redirectURL =
      getURLResponseData.account.items[0]?.policy?.policy_change?.redirect_url;
    errorMessage =
      getURLResponseData.account.items[0]?.policy?.policy_change?.error_message;
  }

  return (
    <ChangePolicyDropdown
      policyNumber={policyNumber}
      accountNumber={accountNumber}
      sourceSysCode={code}
      clChangeWithDocument={clChangeWithDocument}
      makeAPolicyChange={getURL}
      maxEffectiveDate={maxEffectiveDate}
      minEffectiveDate={minEffectiveDate}
      test-attr="policy-change-dropdown"
      renewalDate={renewalDate}
      errorMessage={errorMessage}
      status={status}
      policyType={type}
      returnedMessage={returnedMessages}
      pcOutage={policyCenterOutageFlagDetails?.enabled}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      multipleChangesIndicator={multipleChangesIndicator}
      readOnlyAccess={readOnlyAccess}
      readOnlyFeatureFlag={readOnlyFeatureFlag}
      isFarmPolicy={isFarmPolicy}
      isCommercialLine={isCommercialLine}
      isCppLine={isCppLine}
      isWorkerCompLine={isWorkerCompLine}
      latestPolicy={latestPolicy}
      periods={periods}
      isChangeDisabled={isChangeDisabled}
      query_env={query_env}
      line={line}
      uploadToken={uploadToken}
      laterRenewalExists={laterRenewalExists}
      activePolicyChanges={activePolicyChanges}
      setRunSubscription={setRunSubscription}
      changeResponse={changeResponse}
      uploadDoc={uploadDoc}
      uploadError={error}
      openChanges={openChanges}
      renewalTerms={termList}
      showRenewal={showRenewal}
      changeDisabledForWC={changeDisabledForWC}
      changeDisabledForCPP={changeDisabledForCPP}
      migrated={migratedTxn || migratingTxn}
    />
  );
};
